// Calendar dates are represented at UTC midnight; the cutoff uses Japan time.
export function shippingDate(now) {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hourCycle: 'h23'
  }).formatToParts(now).map(p => [p.type, p.value]));
  const day = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
  if (+p.hour >= 12) day.setUTCDate(day.getUTCDate() + 1);
  while (day.getUTCDay() === 0) day.setUTCDate(day.getUTCDate() + 1);
  return day;
}

export function earliestSlot(now, carrierLeads) {
  if (!carrierLeads.length || carrierLeads.some(x => !x || !Number.isInteger(x.days) || x.days < 0 || !Number.isInteger(x.earliestHour))) return null;
  const lead = carrierLeads.reduce((latest, x) =>
    x.days * 24 + x.earliestHour > latest.days * 24 + latest.earliestHour ? x : latest
  );
  const day = shippingDate(now);
  day.setUTCDate(day.getUTCDate() + lead.days + 1);
  return { date: day.toISOString().slice(0, 10), earliestHour: lead.earliestHour };
}

export function timeSlots(date, earliest) {
  if (!earliest || date < earliest.date) return [];
  return [8, 14, 16, 18, 19].filter(h => date > earliest.date || h >= earliest.earliestHour);
}
export function normalizePostal(value) {
  return value.normalize('NFKC').replace(/[\s-]/g, '');
}
export function routeLead(record, rsl, yamato, japanPost) {
  if (!record) return null;
  const leads=[];
  if(yamato) {
    if(record[1]<0) return null;
    leads.push({days:record[1],earliestHour:record[2]===99?8:record[2]});
  }
  if(japanPost) {
    const row=rsl.leads[record[0]];
    if(!row) return null;
    leads.push({days:row[0],earliestHour:row[1]});
  }
  return {leads,noTime:yamato&&record[2]===99};
}
// Only replace the block owned by this picker; preserve the customer's message.
export function deliveryOrderNote(original, choice) {
  const start = '【RAYFOOK お届け日時指定】';
  const end = '【お届け日時指定ここまで】';
  const customer = String(original || '').replace(/(?:\r?\n){0,2}【RAYFOOK お届け日時指定】[\s\S]*?【お届け日時指定ここまで】/g, '');
  const postal = normalizePostal(choice.postal || '');
  const block = [start,
    `お届け希望日：${choice.date || '指定なし（最短でお届け）'}`,
    `希望時間帯：${choice.time || '指定なし'}`,
    `確認用郵便番号：${postal || '未入力'}`,
    end].join('\n');
  return customer + (customer ? '\n\n' : '') + block;
}
if (typeof customElements !== 'undefined' && !customElements.get('rayfook-delivery-date')) {
  class DeliveryDate extends HTMLElement {
    static choice=null;
    static cache=new Map();
    connectedCallback() {
      this.postal=this.querySelector('#rf-delivery-postal');
      this.date=this.querySelector('#rf-delivery-date');
      this.time=this.querySelector('#rf-delivery-time');
      this.status=this.querySelector('[data-delivery-status]');
      this.error=this.querySelector('[data-delivery-error]');
      this.urls=JSON.parse(this.querySelector('[data-postal-urls]').textContent);
      this.sequence=0;
      this.route=null;
      const saved=DeliveryDate.choice||{postal:this.postal.value,date:this.date.dataset.saved,time:this.time.dataset.saved};
      this.postal.value=saved.postal;
      this.querySelector('[data-check-postal]').onclick=()=>this.lookup();
      this.postal.oninput=()=>{
        ++this.sequence;this.loading=false;this.route=null;this.clear();this.status.textContent='郵便番号を入力し、「日付を確認」を押してください。';this.remember();
      };
      this.postal.onkeydown=e=>{if(e.key==='Enter'){e.preventDefault();this.lookup();}};
      this.date.onchange=()=>{this.fillTimes();this.remember();};
      this.time.onchange=()=>this.remember();
      this.hidden=false;
      if(saved.postal)this.lookup(saved);
    }
    disconnectedCallback(){++this.sequence;}
    remember(){DeliveryDate.choice={postal:this.postal.value,date:this.date.value,time:this.time.value};}
    clear(){this.date.length=1;this.date.value='';this.time.length=1;this.time.value='';}
    async data(url){
      if(DeliveryDate.cache.has(url))return DeliveryDate.cache.get(url);
      const response=await fetch(url);
      if(!response.ok)throw Error('Unable to load lead times');
      const data=await response.json();DeliveryDate.cache.set(url,data);return data;
    }
    async lookup(saved=null){
      const seq=++this.sequence,zip=normalizePostal(this.postal.value);
      this.loading=true;this.route=null;this.clear();this.error.textContent='';
      if(!/^\d{7}$/.test(zip)){this.loading=false;this.status.textContent='7桁の郵便番号を入力してください。';this.remember();return;}
      this.postal.value=zip;this.status.textContent='お届け先を確認しています…';
      try{
        const [shard,rsl]=await Promise.all([this.data(this.urls[zip.slice(0,2)]),this.data(this.dataset.rslUrl)]);
        if(seq!==this.sequence||!this.isConnected)return;
        const record=shard[zip];
        this.loading=false;this.route=routeLead(record,rsl,this.dataset.yamato==='true',this.dataset.rsl==='true');
        if(!this.route){this.status.textContent='この郵便番号の希望日時は確認が必要です。「指定なし」でご注文いただくか、チャットでご相談ください。';this.remember();return;}
        this.checkedZip=zip;this.populate();
        this.status.textContent=(record[0]>=0?rsl.prefectures[record[0]]+'へのお届け。':'')+'希望日を選択できます。';
        if(this.route.noTime)this.status.textContent+='この地域は時間帯指定を承れません。';
        if(saved?.date){
          this.date.value=saved.date;
          if(this.date.selectedIndex<0){this.date.value='';this.error.textContent='指定可能な日付が変わりました。希望日を選び直してください。';}
        }
        this.fillTimes();
        if(saved?.time){this.time.value=saved.time;if(this.time.selectedIndex<0)this.time.value='';}
        this.remember();
      }catch(e){if(seq===this.sequence){this.loading=false;this.route=null;this.clear();this.status.textContent='日付を読み込めませんでした。もう一度「日付を確認」を押してください。';this.remember();}}
    }
    populate(){
      this.date.length=1;
      const first=earliestSlot(new Date(),this.route.leads);
      const date=new Date(first.date+'T00:00:00Z');
      const fmt=new Intl.DateTimeFormat('ja-JP',{timeZone:'UTC',month:'long',day:'numeric',weekday:'short'});
      for(let n=0;n<30;n++){this.date.add(new Option(fmt.format(date),date.toISOString().slice(0,10)));date.setUTCDate(date.getUTCDate()+1);}
    }
    fillTimes(){
      const previous=this.time.value;this.time.length=1;
      if(this.route&&!this.route.noTime){
        const first=earliestSlot(new Date(),this.route.leads);
        const hours=this.date.value?timeSlots(this.date.value,first):[8,14,16,18,19];
        for(const h of hours){const label=h===8?'午前中':`${h}:00〜${h===19?21:h+2}:00`;this.time.add(new Option(label,label));}
      }
      this.time.value=previous;if(this.time.selectedIndex<0)this.time.value='';
    }
    validate(){
      if(this.loading){this.error.textContent='お届け先の確認が終わるまでお待ちください。';return false;}
      if(!this.date.value&&!this.time.value)return true;
      if(!this.route||normalizePostal(this.postal.value)!==this.checkedZip){this.error.textContent='お届け先をもう一度確認してください。';this.postal.focus();return false;}
      const first=earliestSlot(new Date(),this.route.leads);
      const savedDate=this.date.value,savedTime=this.time.value;
      if(savedDate&&savedDate<first.date){this.populate();this.date.value='';this.fillTimes();this.remember();this.error.textContent='受付締切を過ぎました。希望日を選び直してください。';this.date.focus();return false;}
      this.fillTimes();
      if(savedTime&&this.time.value!==savedTime){this.error.textContent='指定可能な時間帯が変わりました。選び直してください。';this.time.focus();this.remember();return false;}
      return true;
    }
  }
  customElements.define('rayfook-delivery-date',DeliveryDate);
  // formdata also covers native form submission. set() ensures exactly one note,
  // including when the theme's optional order-note textarea is switched off.
  document.addEventListener('formdata', event => {
    if (event.target.id !== 'cart') return;
    const picker = document.querySelector('rayfook-delivery-date');
    if (!picker) return;
    const original = event.formData.has('note') ? event.formData.get('note')
      : picker.querySelector('[data-original-note]').value;
    event.formData.set('note', deliveryOrderNote(original, {
      postal: picker.postal.value, date: picker.date.value, time: picker.time.value
    }));
  }, true);
  document.addEventListener('submit',event=>{
    if(event.target.id!=='cart')return;
    const picker=document.querySelector('rayfook-delivery-date');
    if(picker&&!picker.validate()){event.preventDefault();event.stopImmediatePropagation();}
  },true);
}
